import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { FaRegHandPointLeft } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa6";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  fadeInTopVariant,
  fadeInLeftVariant,
  fadeInRightVariant,
} from "../utils/variants";
import { ButtonContainer } from "./Home";
import LanguageSwitch from "../components/LanguageSwitch";
import TagList from "../components/TagList";
import Logo from "../assets/websculpt_logo_black.png";

const WorkDetails = () => {
  const { t } = useTranslation();
  const [background, setBackground] = useState(false);
  const { slug } = useParams();
  const projects = t("works.projects", { returnObjects: true });
  const linkRef = useRef(null);
  const navigate = useNavigate();

  const changeBackground = () => setBackground(window.scrollY >= 60);
  const project = projects.find((p) => p.slug === slug);

  useEffect(() => {
    window.addEventListener("scroll", changeBackground);
    return () => window.removeEventListener("scroll", changeBackground);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") navigate("/works");
  };

  return (
    <MainContainer>
      <HeaderNav className={background ? "header_bg" : ""}>
        <Nav>
          <DivLogo>
            <NavLink to="/">
              <Image src={Logo} alt="WebSculpt Creations logo développeur" />
            </NavLink>
          </DivLogo>
          <RightSide>
            <LanguageSwitch />
          </RightSide>
        </Nav>
      </HeaderNav>

      <SectionWork>
        {/* Colonne gauche — lien retour */}
        <ArticleWorkFirst
          as={motion.article}
          variants={fadeInLeftVariant}
          initial="hidden"
          whileInView="visible"
        >
          <BackLink
            href="/works"
            tabIndex={0}
            ref={linkRef}
            onKeyDown={handleKeyDown}
          >
            <motion.span
              animate={{ x: [0, -4, 0] }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <FaRegHandPointLeft />
            </motion.span>
            {t("work_details.back")}
          </BackLink>
        </ArticleWorkFirst>

        {/* Colonne droite — contenu projet */}
        <ArticleWork>
          <PictureWrapper
            as={motion.div}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Swiper
              pagination={{ clickable: true }}
              modules={[Pagination]}
              style={{ width: "100%", height: "100%" }}
            >
              {project.images.map((img, index) => (
                <SwiperSlide key={index}>
                  <ImgWork src={img.src} alt={img.alt} />
                </SwiperSlide>
              ))}
            </Swiper>
          </PictureWrapper>

          <DivWork
            as={motion.div}
            variants={fadeInTopVariant}
            initial="hidden"
            whileInView="visible"
            transition={{ type: "tween", duration: 1.2, delay: 0.4 }}
          >
            <WorkTitle>{project.title}</WorkTitle>
            <WorkText>{project.fullText}</WorkText>
          </DivWork>

          <TagList items={project.roles} variant="role" />
          <TagList items={project.technologies} variant="tech" />

          <ButtonContainer
            whileHover={{
              scale: 1.05,
              textShadow: "0px 0px 8px rgb(255,255,255)",
              boxShadow: "0px 0px 8px rgba(255,255,255,0.3)",
            }}
            whileTap={{ scale: 0.97 }}
          >
            <Link to={project.externalUrl} className="btn_works">
              {t("work_details.link")}
              <motion.span
                animate={{ x: [0, 6, 0], opacity: [1, 0.5, 1] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <FaArrowRight />
              </motion.span>
            </Link>
          </ButtonContainer>
        </ArticleWork>
      </SectionWork>

      <FooterWork>
        <HR />
        <FooterText>{t("works.category")}</FooterText>
      </FooterWork>
    </MainContainer>
  );
};

export default WorkDetails;

/* ─── Layout ──────────────────────────────────────────────────────────────── */

const MainContainer = styled.main`
  max-width: 85%;
  margin: 0 auto;
  padding-top: 10rem;

  @media (max-width: 1024px) {
    max-width: 92%;
    padding-top: 8rem;
  }

  @media (max-width: 768px) {
    max-width: 94%;
    padding-top: 7rem;
  }

  @media (max-width: 480px) {
    max-width: 95%;
    padding-top: 6rem;
  }
`;

/* ─── Header ──────────────────────────────────────────────────────────────── */

const HeaderNav = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 90px;
  z-index: 10;
  transition:
    background 0.3s ease,
    backdrop-filter 0.3s ease;

  &.header_bg {
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const Nav = styled.nav`
  max-width: 95%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 5rem;
  padding-top: 1rem;
`;

const DivLogo = styled.div`
  width: 10rem;
`;

const Image = styled.img`
  width: 7rem;
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

/* ─── Grille principale ───────────────────────────────────────────────────── */

const SectionWork = styled.section`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 2rem;
  align-items: start;
  padding-bottom: 5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 160px 1fr;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
`;

/* ─── Colonne gauche — lien retour ───────────────────────────────────────── */

const ArticleWorkFirst = styled.article`
  padding-top: 0.4rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: #a8a29e;
  font-family: "Special Elite", system-ui;
  font-size: 1.1rem;
  text-transform: uppercase;
  text-decoration: none;
  transition: color 0.25s ease;

  &:hover {
    color: #fafafa;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

/* ─── Colonne droite — contenu ────────────────────────────────────────────── */

const ArticleWork = styled.article`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* aligné à gauche */

  /* Slider */
  .swiper-pagination-bullet {
    background: #fafafa;
    opacity: 0.5;
  }
  .swiper-pagination-bullet-active {
    background: #fafafa;
    opacity: 1;
  }
  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* Wrapper du slider — remplace .picture avec des tailles fluides */
const PictureWrapper = styled.div`
  width: 100%;
  max-width: 42rem;
  aspect-ratio: 9 / 7;
  border: 2px solid rgba(250, 250, 250, 0.55);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  cursor: pointer;

  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;
  &:hover {
    border-color: rgba(180, 1, 18, 0.5);
    box-shadow: 0 8px 40px rgba(180, 1, 18, 0.15);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ImgWork = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  background: #0a0a0a; /* fond sombre pour les bandes autour de l'image */
`;

/* ─── Texte du projet ─────────────────────────────────────────────────────── */

const DivWork = styled.div`
  width: 100%;
  max-width: 42rem;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const WorkTitle = styled.h2`
  text-transform: uppercase;
  margin-top: 3rem;
  font-size: 2rem;
  letter-spacing: 0.04em;

  /* Ligne décorative rouge sous le titre — cohérent avec Home */
  position: relative;
  display: inline-block;
  padding-bottom: 0.4rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    height: 2px;
    background: linear-gradient(90deg, rgba(180, 1, 18, 0.7), transparent);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-top: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
  }
`;

const WorkText = styled.p`
  color: #a8a29e;
  font-size: 1.2rem;
  line-height: 1.7;
  margin-top: 1rem;

  @media (max-width: 1024px) {
    font-size: 1.05rem;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

/* ─── Footer ──────────────────────────────────────────────────────────────── */

const FooterWork = styled.footer`
  padding: 0.5rem 0 2rem 0;
`;

const HR = styled.hr`
  opacity: 0.2;
  border-color: #fafafa;
`;

const FooterText = styled.p`
  padding-top: 0.5rem;
  width: 80%;
  margin-left: 2rem;
  font-style: italic;
  text-transform: uppercase;
  color: #a8a29e;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-left: 1rem;
    width: 90%;
  }
`;
